import { isDebugMode } from './utils';

type LogMethod = (method: string, ...args: unknown[]) => void;

/**
 * Optional set of options for the logger.
 * - backendLog: If true, logs to the backend (not implemented yet).
 * - pkgNameStyles: CSS styles for the package name in the log output.
 * - fileNameStyles: CSS styles for the file name in the log output.
 */
type PackageLoggerOptions = {
  backendLog?: boolean;
  pkgNameStyles?: string;
  fileNameStyles?: string;
};

export interface Logger {
  (method: string, ...args: unknown[]): void;
  error: LogMethod;
  info: LogMethod;
  warn: LogMethod;
  log: LogMethod;
  debug: LogMethod;
}

/**
 * Default styles for the package name in the log output.
 * The first element is the format string, the second element is the CSS styles.
 */
const defaultPkgNameStyles = [
  '%c%s%c %s',
  'background: green; color: white; padding: 2px 3px 2px 3px; margin-left: 1px; border-radius: 3px;',
];

/**
 * Perform the actual logging.
 */
const logger = (
  packageAcronym: string,
  fileName: string,
  level: 'log' | 'error' | 'info' | 'warn' | 'debug',
  method: string,
  options?: PackageLoggerOptions,
  ...args: unknown[]
) => {
  const {
    pkgNameStyles = '',
    fileNameStyles = 'background: none; color: inherit; font-weight: normal;',
    backendLog = false,
  } = options || {};

  if (isDebugMode() || level === 'error') {
    const spacer = args.length === 0 ? '' : ' ::';
    console[level](
      ...(pkgNameStyles
        ? [defaultPkgNameStyles[0], pkgNameStyles]
        : defaultPkgNameStyles),
      `[${packageAcronym}]`,
      fileNameStyles,
      `[${fileName}]`,
      `${method}${spacer}`,
      ...args,
    );
  }

  // WIP: Implement backend logging.
  if (backendLog) {
    console.log('PLACEHOLDER: backend log will be implemented here');
  }
};

/**
 * Creates a callable logger object for a specific file.
 * The logger will only log messages if __debug_mode is set in localStorage.
 *
 * @param {string} fileName The name of the file to create the logger for.
 * @returns {object} A callable object that can be used to log messages. Handles multiple log levels.
 */
const logObject = (
  packageAcronym: string,
  fileName: string,
  options?: PackageLoggerOptions,
) => {
  const logFunc = (method: string, ...args: unknown[]) => {
    logger(packageAcronym, fileName, 'log', method, options, ...args);
  };
  logFunc.log = (method: string, ...args: unknown[]) => {
    logger(packageAcronym, fileName, 'log', method, options, ...args);
  };
  logFunc.error = (method: string, ...args: unknown[]) => {
    logger(packageAcronym, fileName, 'error', method, options, ...args);
  };
  logFunc.info = (method: string, ...args: unknown[]) => {
    logger(packageAcronym, fileName, 'info', method, options, ...args);
  };
  logFunc.warn = (method: string, ...args: unknown[]) => {
    logger(packageAcronym, fileName, 'warn', method, options, ...args);
  };
  logFunc.debug = (method: string, ...args: unknown[]) => {
    logger(packageAcronym, fileName, 'debug', method, options, ...args);
  };
  return logFunc as Logger;
};

/**
 * Package-level log factory.
 *
 * Returns factory for simple file-level logger scoped to a package acronym
 * (e.g. PBL). The returned factory creates a log object for the file.
 * Outputted logs are prefixed with the package & file names.
 *
 * Only logs if __debug_mode is set in localStorage.
 * - Note: this exception is log.error, which is always logged.
 *
 * Example usage:
 * ```
 * //====== log-local.ts ======
 * import { createPackageLogger } from '@cloudbeds/frontend-logger';
 *
 * // Package level log factory. Used to construct file-level loggers.
 * export const createLogLocal = createPackageLogger('PSDK');
 *
 * // Alternatively, you can use a custom style string for the package name:
 * export const createLogLocal = createPackageLogger('PSDK', 'color: red; font-weight: bold;');
 *
 * //====== PaymentElement.tsx ======
 * import { createLogLocal } from './log-local';
 *
 * // File-level logger. Logs the package name and file name before each log.
 * const logLocal = createLogLocal('PaymentElement');
 *
 * logLocal('onSubmit', 'Payment submitted');
 * // Output: [PSDK] [PaymentElement] onSubmit :: Payment submitted
 *
 * logLocal('onSubmit', 'payment:', payment);
 * // Output: [PSDK] [PaymentElement] onSubmit :: payment: { ... }
 *
 * logLocal.error('onSubmit', 'Error occurred');
 * // Output: [PSDK] [PaymentElement] onSubmit :: Error occurred
 * // Note: in this case, it's logged as an error message, & logs even if __debug_mode isn't set.
 * ```
 *
 * @param {string} packageAcronym An acronym representing the package being logged in.
 * @param {string} options.pkgNameStyles Optional string with the CSS styles to
 *                                       apply to the package name header.
 * @param {boolean} options.backendLog Optional flag to enable backend logging.
 * @param {string} options.fileNameStyles Optional string with the CSS styles to
 *                                       apply to the file name header.
 * @param {string} fileName The name of the file being logged in.
 * @param {string} method The name of the method/function being logged.
 * @param {any[]} args Other data to log. Can accept anything, and any number of arguments.
 *
 * @returns {Logger} A package-level object that can be used to create file-level loggers.
 */
export const createPackageLogger =
  (packageAcronym: string, options?: PackageLoggerOptions) =>
  (fileName: string) =>
    logObject(packageAcronym, fileName, options);
